import React, { useMemo, useState } from 'react';
import styles from './Activity.module.scss';
import { AuditLog } from 'new/entities/AuditLog';
import { Stack } from 'new/elements/Stack';
import { TimeAgo } from 'new/elements/TimeAgo';
import { Skeleton, Typography, TypographyProps } from '@mui/material';
import JSONUtils from 'js/lib/JSONUtils';
import { convertTransformToCode, decodeTranformCode } from 'js/lib/TransformsUtils';
import concat from 'lodash/concat';
import has from 'lodash/has';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isPlainObject from 'lodash/isPlainObject';
import map from 'lodash/map';
import { PARAM_DISPLAY_NAMES } from 'new/elements/Activity/consts/paramsNames';
import reduce from 'lodash/reduce';
import size from 'lodash/size';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import { TransformType } from 'new/components/ReusableTransform/types';
import { DISPLAY_ITEM_TYPES } from 'new/constants/resources';
import { Changes } from 'new/elements/Activity/Changes';
import { Button } from 'new/elements/Button';
import { useUsers } from 'new/hooks/adapters/useUsers';

interface ItemProps {
  item: AuditLog;
  loading?: boolean;
  resourceData: any;
}

const EVENT_DISPLAY_NAMES = {
  update: 'updated',
  create: 'created',
  destroy: 'destroyed',
};

const EVENT_STATUS_DISPLAY_NAMES = {
  INIT: 'initialized',
  ACTIVE: 'activated',
  PAUSED: 'paused',
  RATE_LIMITED: 'rate limited',
  DEACTIVATED: 'deactivated',
  TRIAL_EXPIRED: 'trial expired',
};

const getChangedItems = (changes: any = {}, item_type, e, resourceData: any = {}) => {
  let itemsToExclude = ['updated_at', 'created_at', 'id'];
  switch (item_type) {
    case 'DataSource':
      itemsToExclude = concat(itemsToExclude, [
        'resource_type',
        'source_schema',
        'data_samples',
        'resource_id',
        'source_schema_id',
        'ingest_method',
        'source_format',
        'source_type',
      ]);

      break;
    case 'DataSet':
      itemsToExclude = concat(itemsToExclude, [
        'resource_type',
        'source_schema',
        'data_samples',
        'resource_id',
        'source_schema_id',
      ]);
      break;

    case 'DataSink':
      itemsToExclude = concat(itemsToExclude, [
        'resource_type',
        'source_schema',
        'data_samples',
        'resource_id',
        'source_schema_id',
        'ingest_method',
        'source_format',
        'data_sub_id',
        'sink_type',
      ]);
      break;

    case 'CodeContainer':
      itemsToExclude = concat(itemsToExclude, ['output_type', 'reusable', 'code_encoding']);
      break;

    case 'DataSchema':
      itemsToExclude = concat(itemsToExclude, ['data_samples', 'template']);
      break;
    default:
  }

  let shouldUpdateCodeLanguage = false;
  let newCodeLanguage = { from: '', to: '' };
  let changesArr = reduce(
    changes,
    (acc, o: any[], key) => {
      if (includes(itemsToExclude, key)) {
        return acc;
      }

      let from = !isEmpty(o) ? o[0] : null;
      let to = !isEmpty(o) && o.length > 1 ? o[1] : null;
      let isDiffEditor = false;

      if (isPlainObject(from)) {
        if (item_type === 'CodeContainer' && key === 'custom_config') {
          if (has(from, 'ui.txArgs.items')) {
            from = from.ui.txArgs.items;
          }
        }
        from = JSONUtils.stringifyJson(from, '  ');
        isDiffEditor = true;
      } else if (item_type === 'CodeContainer' && key === 'code') {
        let encoding = null;
        if (resourceData.output_type === TransformType.ATTRIBUTE) {
          if (has(changes, 'code_encoding') && !isEmpty(changes.code_encoding)) {
            encoding = changes.code_encoding[0];
          } else if (has(resourceData, 'code_encoding')) {
            encoding = resourceData.code_encoding;
          }

          from = decodeTranformCode(from, encoding);
        } else if (resourceData.output_type === TransformType.RECORD && from !== null) {
          let { code: fromCode, codeLanguage: fromCodeLanguage } = convertTransformToCode(from);
          from = fromCode;
          shouldUpdateCodeLanguage = true;
          newCodeLanguage.from =
            fromCodeLanguage === 'javascript' ||
            fromCodeLanguage === 'python' ||
            fromCodeLanguage === 'python3'
              ? fromCodeLanguage
              : 'Nexla JSON';
        }

        isDiffEditor = true;
      }

      if (isPlainObject(to)) {
        if (item_type === 'CodeContainer' && key === 'custom_config') {
          if (has(to, 'ui.txArgs.items')) {
            to = to.ui.txArgs.items;
          }
        }
        to = JSONUtils.stringifyJson(to, '  ');
        isDiffEditor = true;
      } else if (item_type === 'CodeContainer' && key === 'code') {
        let encoding = null;
        if (resourceData.output_type === TransformType.ATTRIBUTE) {
          if (
            has(changes, 'code_encoding') &&
            !isEmpty(changes.code_encoding) &&
            changes.code_encoding.length > 1
          ) {
            encoding = changes.code_encoding[1];
          } else if (has(resourceData, 'code_encoding')) {
            encoding = resourceData.code_encoding;
          }
          to = decodeTranformCode(to, encoding);
        } else if (resourceData.output_type === TransformType.RECORD && to !== null) {
          let { code: toCode, codeLanguage: toCodeLanguage } = convertTransformToCode(to);
          to = toCode;
          shouldUpdateCodeLanguage = true;
          newCodeLanguage.to =
            toCodeLanguage === 'javascript' ||
            toCodeLanguage === 'python' ||
            toCodeLanguage === 'python3'
              ? toCodeLanguage
              : 'Nexla JSON';
        }

        isDiffEditor = true;
      }

      let keyDisplay = has(PARAM_DISPLAY_NAMES, `${item_type}.${key}`)
        ? PARAM_DISPLAY_NAMES[item_type][key]
        : startCase(key);

      acc.push({
        key: key,
        fromValue: from,
        toValue: to,
        keyDisplay: keyDisplay,
        isDiffEditor,
      });
      return acc;
    },
    [],
  );
  if (shouldUpdateCodeLanguage) {
    changesArr = map(changesArr, (ch) => {
      if (ch.key !== 'code_type') {
        return ch;
      }

      return {
        ...ch,
        fromValue: newCodeLanguage.from,
        toValue: newCodeLanguage.to,
      };
    });
  }

  return changesArr;
};

const getItemChangeSummary = (item: AuditLog = null, objectChanges = []) => {
  const eventDisplayName = EVENT_DISPLAY_NAMES[item.event];
  let summary = eventDisplayName;
  if (!isNil(item) && has(item, 'change_summary') && !isEmpty(objectChanges)) {
    if (size(objectChanges) === 1 && objectChanges[0].key === 'status') {
      let newStatus = objectChanges[0].toValue;
      summary = has(EVENT_STATUS_DISPLAY_NAMES, newStatus)
        ? EVENT_STATUS_DISPLAY_NAMES[newStatus]
        : toLower(newStatus);
    } else {
      summary =
        item.change_summary.join(', ').replace(/, ([^,]*)$/, ' and $1') + ` ${eventDisplayName}`;
    }
  }

  return summary;
};

const Summary = (props: ItemProps & { objectChanges: any } & TypographyProps) => {
  const { item, objectChanges, resourceData, ...rest } = props;
  const users = useUsers(true);
  const { impersonator, user, user_email, item_type } = item;
  let actionBy = null;

  const itemName = DISPLAY_ITEM_TYPES[item_type] || item_type;
  const changeSummary = getItemChangeSummary(item, objectChanges);

  if (impersonator) {
    let thisUser = users?.[impersonator];
    actionBy =
      impersonator === 1 || thisUser.email === 'admin@nexla.com'
        ? 'Nexla Platform'
        : has(thisUser, 'full_name')
          ? `${thisUser.full_name} (${thisUser.email})`
          : thisUser.email;
  } else if (user && has(users, user)) {
    let thisUser = users[user];
    actionBy =
      user === 1 || thisUser.email === 'admin@nexla.com'
        ? 'Nexla Platform'
        : has(thisUser, 'full_name')
          ? `${thisUser.full_name} (${thisUser.email})`
          : thisUser.email;
  } else {
    actionBy = user_email === 'admin@nexla.com' ? 'Nexla Platform' : user_email;
  }

  actionBy = !isEmpty(actionBy) ? `by ${actionBy}` : '';

  return (
    <Typography variant="body3" {...rest}>
      {itemName} {changeSummary} {actionBy}
    </Typography>
  );
};

const Loader = () => {
  return (
    <Stack className={styles.info} gap="8px">
      <Stack gap="4px">
        <Skeleton animation="wave" variant="rounded" width="150px" />
        <Skeleton animation="wave" variant="rounded" width="100%" className={styles.summary} />
      </Stack>
      <Stack marginBottom="16px">
        <Skeleton animation="wave" variant="rounded" width="120px" />
      </Stack>
    </Stack>
  );
};

export const Item = (props: ItemProps) => {
  const [showChanges, setShowChanges] = useState(false);
  const { item, resourceData, loading } = props;
  const { item_type, object_changes, event } = item;

  const objectChanges = useMemo(
    () => getChangedItems(object_changes, item_type, event, resourceData),
    [object_changes, item_type, event, resourceData],
  );

  const toggleChanges = () => {
    setShowChanges(!showChanges);
  };

  if (!loading && (!objectChanges || !objectChanges.length) && event !== 'destroy') {
    return null;
  }

  return (
    <Stack className={styles.item} direction="row" alignItems="baseline" gap="28px">
      <Stack className={styles.timelineDots}>
        <div className={styles.dot} />
        <div className={styles.line} />
      </Stack>
      <Stack className={styles.content}>
        {loading ? (
          <Loader />
        ) : (
          <Stack className={styles.info}>
            <TimeAgo date={item.created_at} variant="body3" className={styles.time} />
            <Summary className={styles.summary} {...props} objectChanges={objectChanges} />
            {objectChanges?.length ? (
              <Button onClick={toggleChanges} variant="text" color="primary">
                {showChanges ? 'Hide Details' : 'View Details'}
              </Button>
            ) : null}
          </Stack>
        )}
        {showChanges && objectChanges?.length ? (
          <Changes className={styles.changes} objectChanges={objectChanges} event={event} />
        ) : null}
      </Stack>
    </Stack>
  );
};
